import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@services/api';
import type { POMatchingResult } from '../types/index';
import { DataTable } from '@components/DataTable';
import { Search, Download, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';

export const POMatching: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedResult, setSelectedResult] = useState<POMatchingResult | null>(null);

  // Fetch PO matching results from live database
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['po-matching', page, pageSize],
    queryFn: async () => {
      try {
        // This would be a new endpoint to get all matching results
        // For now, we'll use a mock implementation
        return {
          data: [],
          pagination: { page, pageSize, total: 0 },
        };
      } catch (err: any) {
        throw new Error(
          err.response?.data?.error?.message || 'Failed to fetch matching results'
        );
      }
    },
    staleTime: 30000,
    retry: 3,
  });

  // Filter results based on search and status
  const filteredResults = React.useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((result: POMatchingResult) => {
      const matchesSearch =
        result.invoiceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.poId.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || result.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const handleExport = async () => {
    try {
      const csvData = await apiClient.exportLogs('csv');
      const url = window.URL.createObjectURL(new Blob([csvData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `po-matching-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'matched':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'manual_review':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      key: 'invoiceId' as const,
      label: 'Invoice ID',
      width: '150px',
    },
    {
      key: 'poId' as const,
      label: 'PO ID',
      width: '150px',
    },
    {
      key: 'matchScore' as const,
      label: 'Match Score',
      width: '120px',
      render: (value: number) => {
        const percentage = (value * 100).toFixed(0);
        const color =
          value >= 0.85 ? 'text-green-600' : value >= 0.7 ? 'text-yellow-600' : 'text-red-600';
        return <span className={`font-semibold ${color}`}>{percentage}%</span>;
      },
    },
    {
      key: 'supplierMatch' as const,
      label: 'Supplier',
      width: '100px',
      render: (value: number) => `${(value * 100).toFixed(0)}%`,
    },
    {
      key: 'amountMatch' as const,
      label: 'Amount',
      width: '100px',
      render: (value: number) => `${(value * 100).toFixed(0)}%`,
    },
    {
      key: 'lineItemsMatch' as const,
      label: 'Line Items',
      width: '100px',
      render: (value: number) => `${(value * 100).toFixed(0)}%`,
    },
    {
      key: 'status' as const,
      label: 'Status',
      width: '120px',
      render: (value: string) => (
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(
            value
          )}`}
        >
          {value.replace('_', ' ').charAt(0).toUpperCase() +
            value.replace('_', ' ').slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">PO Matching Results</h1>
          <p className="text-gray-600 mt-1">
            View and manage invoice to purchase order matching results
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by invoice or PO..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="matched">Matched</option>
              <option value="rejected">Rejected</option>
              <option value="manual_review">Manual Review</option>
            </select>

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => refetch()}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Results Info */}
          <div className="text-sm text-gray-600">
            {isLoading ? (
              'Loading...'
            ) : error ? (
              <span className="text-red-600">Error: {error.message}</span>
            ) : (
              <>
                Showing {filteredResults.length} of{' '}
                {data?.pagination?.total || 0} matching results
              </>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredResults}
            loading={isLoading}
            error={error?.message}
            rowKey="id"
            onRowClick={(result) => setSelectedResult(result)}
            pagination={{
              page,
              pageSize,
              total: data?.pagination?.total || 0,
              onPageChange: setPage,
            }}
          />
        </div>

        {/* Matching Result Detail Modal */}
        {selectedResult && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  Matching Details
                </h2>
                <button
                  onClick={() => setSelectedResult(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Invoice ID</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedResult.invoiceId}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">PO ID</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedResult.poId}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <div className="mt-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(
                          selectedResult.status
                        )}`}
                      >
                        {selectedResult.status.replace('_', ' ').charAt(0).toUpperCase() +
                          selectedResult.status.replace('_', ' ').slice(1)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Overall Score</p>
                    <p className="text-lg font-semibold text-green-600">
                      {(selectedResult.matchScore * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>

                {/* Matching Breakdown */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-semibold text-gray-900 mb-3">
                    Matching Breakdown
                  </p>
                  <div className="space-y-2">
                    {/* Supplier Match */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600">Supplier Match</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {(selectedResult.supplierMatch * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${selectedResult.supplierMatch * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Amount Match */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600">Amount Match</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {(selectedResult.amountMatch * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{
                            width: `${selectedResult.amountMatch * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Line Items Match */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600">Line Items Match</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {(selectedResult.lineItemsMatch * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-yellow-600 h-2 rounded-full"
                          style={{
                            width: `${selectedResult.lineItemsMatch * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Date Match */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600">Date Match</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {(selectedResult.dateMatch * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-purple-600 h-2 rounded-full"
                          style={{
                            width: `${selectedResult.dateMatch * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-gray-200 pt-4 flex gap-2">
                  {selectedResult.status === 'pending' && (
                    <>
                      <button className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition flex items-center justify-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Approve Match
                      </button>
                      <button className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition flex items-center justify-center gap-2">
                        <XCircle className="w-4 h-4" />
                        Reject Match
                      </button>
                    </>
                  )}
                  {selectedResult.status === 'manual_review' && (
                    <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition">
                      Review Details
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
